/**
 * NBM Recently Viewed
 *
 * Reads `nbm-recently-viewed` from localStorage (populated by product-detail.js).
 * Each entry is { id, handle }. For legacy entries that only have a `url`,
 * the handle is extracted from the URL path.
 *
 * Each product card is rendered server-side via the Section Rendering API
 * (/products/{handle}?section_id=product-card-section) so prices are always
 * in the active currency/market context.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'nbm-recently-viewed';

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function getHandle(item) {
    if (item.handle) return item.handle;
    // Legacy items stored with full URL — extract handle from path
    const url = item.url || '';
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function fetchCard(handle) {
    return fetch('/products/' + handle + '?section_id=product-card-section', {
      credentials: 'same-origin'
    })
      .then((res) => (res.ok ? res.text() : null))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const item = doc.querySelector('.product-card-section-item');
        return item ? item.innerHTML : null;
      })
      .catch(() => null);
  }

  /* ------------------------------------------------------------------ */
  /*  Carousel controller                                                */
  /* ------------------------------------------------------------------ */

  const CHEVRON_LEFT =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" ' +
    'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="15 18 9 12 15 6"></polyline></svg>';

  const CHEVRON_RIGHT =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" ' +
    'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="9 18 15 12 9 6"></polyline></svg>';

  function initCarousel(wrap) {
    const track = wrap.querySelector('.carousel__track');
    if (!track) return;

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'rv-arrow rv-arrow--prev';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.innerHTML = CHEVRON_LEFT;

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'rv-arrow rv-arrow--next';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.innerHTML = CHEVRON_RIGHT;

    wrap.appendChild(prevBtn);
    wrap.appendChild(nextBtn);

    const dotsEl = document.createElement('div');
    dotsEl.className = 'rv-dots';
    wrap.appendChild(dotsEl);

    if (window.NBMTheme && window.NBMTheme.initCarousel) {
      window.NBMTheme.initCarousel({ track, prevBtn, nextBtn, dotsContainer: dotsEl });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    const section = document.querySelector('[data-recently-viewed]');
    if (!section) return;

    let items;
    try {
      items = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (error) {
      items = [];
    }

    if (items.length === 0) return;

    // Filter out the product currently being viewed
    const detailEl = document.querySelector('[data-product-detail]');
    const currentId = detailEl ? detailEl.getAttribute('data-product-id') : null;
    if (currentId) {
      items = items.filter((product) => String(product.id) !== String(currentId));
    }

    if (items.length === 0) return;

    // Apply section limit
    const limit = parseInt(section.getAttribute('data-limit'), 10) || 10;
    items = items.slice(0, limit);

    const track = section.querySelector('.carousel__track');
    if (!track) return;

    // Fetch all cards in parallel, preserving order
    const fetches = items.map((item) => {
      const handle = getHandle(item);
      return handle ? fetchCard(handle) : Promise.resolve(null);
    });

    Promise.all(fetches).then((results) => {
      const slides = results
        .map((html) => {
          if (!html) return null;
          const slide = document.createElement('div');
          slide.className = 'carousel__item product-carousel__item';
          slide.innerHTML = html;
          return slide;
        })
        .filter(Boolean);

      if (slides.length === 0) return;

      const fragment = document.createDocumentFragment();
      for (const slide of slides) fragment.appendChild(slide);
      track.appendChild(fragment);

      // Show section
      section.removeAttribute('hidden');

      // Sync wishlist icons
      if (window.NBMWishlist) {
        const ids = new Set(window.NBMWishlist.getAll().map((product) => String(product.id || product)));
        section.querySelectorAll('.js-wishlist-toggle').forEach((btn) => {
          const active = ids.has(btn.getAttribute('data-product-id'));
          btn.classList.toggle('is-wishlisted', active);
          const svg = btn.querySelector('svg');
          if (svg) {
            svg.style.fill = active ? '#e11d48' : 'none';
            svg.style.stroke = active ? '#e11d48' : 'currentColor';
          }
        });
      }

      // Init carousel
      const wrap = section.querySelector('.recently-viewed__carousel-wrap');
      if (wrap) initCarousel(wrap);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
